// Camera rig: smooth orbit + zoom with damping, pointer and wheel input.

import * as THREE from "three";

export class CameraRig {
  constructor(camera, domElement) {
    this.camera = camera;
    this.dom = domElement;
    this.target = new THREE.Vector3(0, 0, 0);
    this.theta = Math.PI / 5.2;
    this.phi = Math.PI / 2.6;
    this.radius = 7;
    this.minRadius = 2.4;
    this.maxRadius = 40;
    this.goalTheta = this.theta;
    this.goalPhi = this.phi;
    this.goalRadius = this.radius;
    this.damping = 0.12;
    this.enabled = true;

    this._pointers = new Map();
    this._pinch = null;
    this._dragging = false;

    domElement.addEventListener("pointerdown", (e) => this._onDown(e));
    domElement.addEventListener("pointermove", (e) => this._onMove(e));
    domElement.addEventListener("pointerup", (e) => this._onUp(e));
    domElement.addEventListener("pointercancel", (e) => this._onUp(e));
    domElement.addEventListener("wheel", (e) => this._onWheel(e), {
      passive: false,
    });
  }

  _onDown(e) {
    if (!this.enabled) return;
    this.dom.setPointerCapture(e.pointerId);
    this._pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this._pointers.size === 2) {
      const [a, b] = [...this._pointers.values()];
      this._pinch = {
        startDist: Math.hypot(a.x - b.x, a.y - b.y),
        startRadius: this.goalRadius,
      };
    } else {
      this._pinch = null;
    }
  }

  _onMove(e) {
    if (!this._pointers.has(e.pointerId)) return;
    const prev = this._pointers.get(e.pointerId);
    const dx = e.clientX - prev.x;
    const dy = e.clientY - prev.y;
    this._pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (this._pointers.size === 1 && !this._pinch) {
      // Longitude runs in the opposite screen-space direction after a pole
      // crossing. Flip its input there so horizontal dragging stays natural.
      const hemisphere = Math.sin(this.goalPhi) >= 0 ? 1 : -1;
      this.goalTheta -= dx * 0.0055 * hemisphere;
      this.goalPhi -= dy * 0.0055;
    } else if (this._pointers.size === 2) {
      const [a, b] = [...this._pointers.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (this._pinch && dist > 0) {
        this.goalRadius = THREE.MathUtils.clamp(
          (this._pinch.startRadius * this._pinch.startDist) / dist,
          this.minRadius,
          this.maxRadius,
        );
      }
    }
  }

  _onUp(e) {
    this._pointers.delete(e.pointerId);
    if (this._pointers.size < 2) this._pinch = null;
  }

  _onWheel(e) {
    if (!this.enabled) return;
    e.preventDefault();
    this.goalRadius = THREE.MathUtils.clamp(
      this.goalRadius * (1 + Math.sign(e.deltaY) * 0.08),
      this.minRadius,
      this.maxRadius,
    );
  }

  // Returns true when the camera moved this frame (used to skip pointer
  // selection after orbit drags).
  update() {
    const k = 1 - Math.pow(1 - this.damping, 1);
    this.theta += (this.goalTheta - this.theta) * k;
    this.phi += (this.goalPhi - this.phi) * k;
    this.radius += (this.goalRadius - this.radius) * k;
    const moved =
      Math.abs(this.goalTheta - this.theta) > 1e-5 ||
      Math.abs(this.goalPhi - this.phi) > 1e-5 ||
      Math.abs(this.goalRadius - this.radius) > 1e-4;
    const sinPhi = Math.sin(this.phi);
    const cosPhi = Math.cos(this.phi);
    const sinTheta = Math.sin(this.theta);
    const cosTheta = Math.cos(this.theta);
    this.camera.position.set(
      this.target.x + this.radius * sinPhi * sinTheta,
      this.target.y + this.radius * cosPhi,
      this.target.z + this.radius * sinPhi * cosTheta,
    );
    // Follow the sphere's tangent instead of a fixed world-up vector. This
    // keeps the view continuous while crossing either pole, allowing vertical
    // orbit to rotate freely just like horizontal orbit.
    this.camera.up
      .set(-cosPhi * sinTheta, sinPhi, -cosPhi * cosTheta)
      .normalize();
    this.camera.lookAt(this.target);
    return moved;
  }

  fitCube(N, { immediate = false } = {}) {
    const radius = Math.max(7.2, N * 2.75);
    this.minRadius = Math.max(4.5, N * 1.4);
    this.maxRadius = Math.max(40, N * 4.5);
    this.goalRadius = radius;
    if (immediate) this.radius = radius;
  }
}
